<!-- eslint-disable vue/no-restricted-html-elements -->
<!-- eslint-disable max-len -->
<template>
  <div class="site-header-container">
    <header
      ref="siteHeader"
      class="site-header"
      :class="[
        $class(headerState),
        {
          ['site-header--overlay-active']: mobileHeaderActive,
        },
      ]"
      data-sign-post-priority="1"
      :js-sign-post="$string('accessibility.sign_posts.site_header')"
      js-overlap-element
      role="banner"
    >
      <!-- Pre-header Banner -->
      <pre-header-banner
        v-if="showPreHeaderBanner && bannerDismissed !== preHeaderBannerId"
        :autoplay-speed="preHeaderBannerInterval"
        :banners="preHeaderBannerContent"
        :pre-header-banner-dismissable="preHeaderBannerDismissable"
        :pre-header-banner-id="preHeaderBannerId"
        :pre-header-banner-theme="preHeaderBannerTheme"
        :pre-header-transition-speed="preHeaderTransitionSpeed"
        @announcement-height="updateAnnouncementHeight"
      />
      <div class="site-header__side site-header__side--left">
        <button
          type="button"
          class="site-header__button site-header__menu"
          :aria-label="$string('general.site_header.menu')"
          @click="
            toggleOverlay({
              component: 'menu-drawer',
              ignoreDismissed: true,
              namespace: 'menuDrawer',
            })
          "
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="site-header__menu-hamburger"
          >
            <line
              x1="1"
              y1="8"
              x2="15"
              y2="8"
              stroke="currentColor"
            />
            <line
              x1="1"
              y1="4.5"
              x2="15"
              y2="4.5"
              stroke="currentColor"
            />
            <line
              x1="1"
              y1="11.5"
              x2="15"
              y2="11.5"
              stroke="currentColor"
            />
          </svg>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="site-header__menu-close"
          >
            <path
              d="M3.0498 12.9498L12.9493 3.05027"
              stroke="currentColor"
            />
            <path
              d="M12.9502 12.9498L3.0507 3.05027"
              stroke="currentColor"
            />
          </svg>
        </button>

        <button
          type="button"
          class="site-header__button site-header__search"
          @click="
            toggleOverlay({
              component: 'search-drawer',
              ignoreDismissed: true,
              namespace: 'searchDrawer',
            })
          "
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.5 7.5C1.5 10.8137 4.18629 13.5 7.5 13.5C10.8137 13.5 13.5 10.8137 13.5 7.5C13.5 4.18629 10.8137 1.5 7.5 1.5C4.18629 1.5 1.5 4.18629 1.5 7.5Z"
              stroke="currentColor"
            />
            <rect
              x="11"
              y="12.1313"
              width="1.6"
              height="4"
              transform="rotate(-45 11 12.1313)"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
      <div class="site-header__logo site-header-logo">
        <a
          class="site-header__link site-header-logo__link"
          :href="$variable('routes.root')"
          :aria-label="$string('general.site_header.return_home')"
        >
          <div
            class="site-header-logo__media"
            v-html="$variable('design.logo_primary')"
          />
        </a>
      </div>

      <div class="site-header__side site-header__side--right">
        <button
          v-if="displayNavElements.displaySearch"
          type="button"
          class="site-header__button site-header__search"
          :aria-label="$string('general.site_header.search')"
          @click="
            toggleOverlay({
              component: 'search-drawer',
              ignoreDismissed: true,
              namespace: 'searchDrawer',
            })
          "
        >
          <span
            :data-title="$string('general.navigation.search')"
            v-text="$string('general.navigation.search')"
          />
        </button>

        <template v-if="displayNavElements.displayAccount">
          <a
            class="site-header__link site-header__account"
            :href="$variable('routes.account.url')"
          >
            <span
              :data-title="$string('general.navigation.account')"
              v-text="$string('general.navigation.account')"
            />
          </a>
        </template>

        <button
          v-if="displayNavElements.displayWishlist"
          class="site-header__button"
          :class="{
            [$class('active')]: getActiveOverlay === 'wishlistDrawer',
            'site-header__menu--active': mobileHeaderActive,
          }"
          type="button"
          :aria-label="$string('general.site_header.wishlist')"
          @click="
            toggleOverlay({
              component: 'wishlist-list',
              ignoreDismissed: true,
              namespace: 'wishlistDrawer',
            })"
        >
          <span class="site-header__wishlist-badge__span">

            <component
              :is="wishlistIcon"
            />
            <component
              :is="wishlistFilledIcon"
              class="icon--filled"
            />

            <span
              v-if="getActiveOverlay !== 'wishlistDrawer'"
              class="site-header__wishlist-badge"
              aria-hidden="true"
              v-html="wishlistCount"
            />
          </span>
        </button>
        <button
          v-if="displayNavElements.displayBag"
          type="button"
          class="site-header__navigation-link site-header__bag-btn site-header__button"
          :aria-label="$string('general.navigation.bag')"
          @click="handleCartToggle"
        >
          <span class="site-header__cart-badge__span">
            <template v-if="getActiveOverlay === 'cartDrawer'">
              <icon-close />
            </template>
            <template v-else>
              <icon-bag v-if="!cart.item_count" />
              <icon-bag-filled v-if="cart.item_count" />
              <span
                v-if="cart.item_count"
                class="site-header__cart-badge text-mobile-mini-mobile"
                aria-hidden="true"
                v-text="cart.item_count"
              />
            </template>
          </span>
        </button>
      </div>

      <div class="site-header__nav site-header-nav">
        <ul class="site-header-nav__items">
          <template
            v-for="primary of desktop.items"
            :key="primary.handle"
          >
            <li
              class="site-header-nav__item"
              :data-handle="primary.handle"
              data-gtm-nav-item
              @mouseleave="revertMediaObject"
            >
              <a
                :href="primary.url"
                class="site-header__link site-header-nav__link"
                data-gtm-nav-level="primary"
                @click="(event) => handleGtm(event)"
              >
                <span
                  :data-title="primary.title"
                  v-text="primary.title"
                />
              </a>
              <div
                v-if="primary.links.length > 0"
                class="site-header-subnav"
              >
                <template
                  v-for="(secondary, idx) of primary.links"
                  :key="secondary.handle"
                >
                  <div
                    v-if="secondary.image"
                    ref="mediaBlock"
                    :data-media-block-handle="`${primary.handle}-${idx}`"
                    class="site-header-subnav-media-block"
                    :class="{
                      'site-header-subnav-media-block--wide': secondary.title_raw.includes('--span-2')
                    }"
                  >
                    <div class="site-header-subnav-media-block__media">
                      <img
                        :src="secondary.image"
                        alt=""
                        width="600"
                        height="800"
                        loading="lazy"
                        class="site-header-subnav-media-block__img"
                        data-block-image
                      >
                    </div>
                    <div class="site-header-subnav-media-block__body">
                      <template
                        v-for="tertiary of secondary.links"
                        :key="tertiary.handle"
                      >
                        <a
                          :href="tertiary.url"
                          class="site-header-subnav-media-block__link"
                          :class="{
                            'site-header-subnav-media-block__link--white': tertiary.title_raw.includes('--white')
                          }"
                          data-block-link
                        >
                          <span
                            data-block-link-text
                            v-text="tertiary.title"
                          />
                          <svg
                            width="16"
                            height="17"
                            viewBox="0 0 16 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fill-rule="evenodd"
                              clip-rule="evenodd"
                              d="M9.72849 8.98542C9.81736 8.94837 9.90674 8.91246 9.9966 8.87769C10.0696 8.84947 10.1428 8.82201 10.2164 8.79531L11 8.51099L10.2164 8.22666C10.1428 8.19996 10.0696 8.1725 9.9966 8.14428C9.90674 8.10952 9.81736 8.0736 9.72849 8.03655C8.69235 7.60459 7.72401 7.0177 6.85395 6.29554C6.55749 6.04947 6.27243 5.78769 6 5.51099L6 11.511C6.27243 11.2343 6.55749 10.9725 6.85395 10.7264C7.72401 10.0043 8.69235 9.41739 9.72849 8.98542Z"
                              fill="currentColor"
                            />
                          </svg>
                        </a>
                      </template>
                    </div>
                  </div>
                  <div
                    v-else-if="secondary.video"
                    class="site-header-subnav-media-block"
                    :class="{
                      'site-header-subnav-media-block--wide': secondary.title_raw.includes('--span-2')
                    }"
                  >
                    <div class="site-header-subnav-media-block__media">
                      <video
                        :src="secondary.video"
                        width="600"
                        height="800"
                        loading="lazy"
                        class="site-header-subnav-media-block__img"
                        autoplay
                        loop
                        muted
                        playsinline
                      />
                    </div>
                    <div class="site-header-subnav-media-block__body">
                      <template
                        v-for="tertiary of secondary.links"
                        :key="tertiary.handle"
                      >
                        <a
                          :href="tertiary.url"
                          class="site-header-subnav-media-block__link"
                          :class="{
                            'site-header-subnav-media-block__link--white': tertiary.title_raw.includes('--white')
                          }"
                        >
                          <span
                            v-text="tertiary.title"
                          />
                          <svg
                            width="16"
                            height="17"
                            viewBox="0 0 16 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fill-rule="evenodd"
                              clip-rule="evenodd"
                              d="M9.72849 8.98542C9.81736 8.94837 9.90674 8.91246 9.9966 8.87769C10.0696 8.84947 10.1428 8.82201 10.2164 8.79531L11 8.51099L10.2164 8.22666C10.1428 8.19996 10.0696 8.1725 9.9966 8.14428C9.90674 8.10952 9.81736 8.0736 9.72849 8.03655C8.69235 7.60459 7.72401 7.0177 6.85395 6.29554C6.55749 6.04947 6.27243 5.78769 6 5.51099L6 11.511C6.27243 11.2343 6.55749 10.9725 6.85395 10.7264C7.72401 10.0043 8.69235 9.41739 9.72849 8.98542Z"
                              fill="currentColor"
                            />
                          </svg>
                        </a>
                      </template>
                    </div>
                  </div>
                  <div
                    v-else
                    class="site-header-subnav-block"
                    data-gtm-subnav-block
                  >
                    <div class="site-header-subnav-block__header">
                      <span
                        class="site-header-subnav-block__link"
                        data-gtm-subnav-block-header
                        v-text="secondary.title"
                      />
                    </div>

                    <ul
                      v-if="secondary.links.length > 0"
                      class="site-header-subnav-block__items"
                    >
                      <li
                        v-for="tertiary of secondary.links"
                        :key="tertiary.handle"
                        class="site-header-subnav-block__item"
                        :class="{
                          'site-header-subnav-block__header': isSubtitle(tertiary),
                          'site-header-subnav-block__footer': isFooter(tertiary),
                        }"
                      >
                        <span
                          v-if="isSubtitle(tertiary)"
                          :href="tertiary.url"
                          class="site-header-subnav-block__link"
                          :class="{
                            'site-header-subnav-block__link--new': isNew(tertiary)
                          }"
                          v-text="tertiary.title"
                        />
                        <a
                          v-else-if="isPrivateSale(tertiary)"
                          :href="tertiary.url"
                          class="site-header-subnav-block__link"
                          data-gtm-nav-level="secondary"
                          @click="(event) => handleGtm(event)"
                          @mouseenter="handleMediaChange(tertiary.handle)"
                        >
                          {{ tertiary.title }}
                        </a>
                        <a
                          v-else-if="isPublicLink(tertiary)"
                          :href="tertiary.url"
                          class="site-header-subnav-block__link"
                          data-gtm-nav-level="secondary"
                          @click="(event) => handleGtm(event)"
                          @mouseenter="handleMediaChange(tertiary.handle)"
                        >
                          {{ tertiary.title }}
                          <span
                            v-if="isNew(tertiary)"
                            class="site-header-subnav-block__new-badge"
                            v-text="$string('general.site_header.new_flag')"
                          />
                        </a>
                      </li>
                    </ul>
                  </div>
                </template>
              </div>
            </li>
          </template>
        </ul>
      </div>

      <div
        class="site-header__scroll-content"
        hidden
      >
        <!-- Pre-header Banner -->
        <pre-header-banner
          v-if="showPreHeaderBanner && bannerDismissed !== preHeaderBannerId"
          :autoplay-speed="preHeaderBannerInterval"
          :banners="preHeaderBannerContent"
          :pre-header-banner-dismissable="preHeaderBannerDismissable"
          :pre-header-banner-id="preHeaderBannerId"
          :pre-header-banner-theme="preHeaderBannerTheme"
          :pre-header-transition-speed="preHeaderTransitionSpeed"
          @announcement-height="updateAnnouncementHeight"
        />

        <div
          class="site-header__masthead"
        >
          <div
            class="site-header__container"
            :class="[
              `section-content-mobile--${headerThemeMobile}`,
              `section-content-desktop--${headerThemeDesktop}`,
            ]"
          >
            <!-- Desktop Navigation Left -->
            <nav
              class="site-header__navigation site-header__navigation--left"
              :aria-label="$string('general.site_header.navigation_left')"
            >
              <ul class="site-header__navigation-list" />
            </nav>

            <!-- Mobile Navigation Left -->
            <nav
              class="
                site-header__navigation-list
                site-header__navigation-list--mobile
              "
              :aria-label="$string('general.site_header.navigation_left')"
            >
              <!-- Menu icon -->
              <button
                v-if="mobile?.items?.length"
                class="site-header__menu site-header__icon--menu"
                :class="{
                  [$class('active')]: getActiveOverlay === 'menuDrawer',
                  'site-header__menu--active': mobileHeaderActive,
                }"
                type="button"
                :aria-label="$string('general.site_header.menu')"
                @click="
                  toggleOverlay({
                    component: 'menu-drawer',
                    ignoreDismissed: true,
                    namespace: 'menuDrawer',
                  })
                "
              >
                <icon-close v-if="getActiveOverlay === 'menuDrawer'" />
                <icon-menu v-else />
              </button>

              <!-- Search icon -->
              <button
                v-if="displayNavElements.displaySearch"
                class="site-header__menu site-header__icon--search"
                :class="{
                  [$class('active')]: getActiveOverlay === 'searchDrawer',
                  'site-header__menu--active': mobileHeaderActive,
                }"
                type="button"
                :aria-label="$string('general.site_header.search')"
                @click="
                  toggleOverlay({
                    component: 'search-drawer',
                    ignoreDismissed: true,
                    namespace: 'searchDrawer',
                  })
                "
              >
                <component :is="searchIcon" />
              </button>
            </nav>

            <!-- Logo -->
            <a
              class="site-header__logo"
              :href="$variable('routes.root')"
              :aria-label="$string('general.site_header.return_home')"
            >
              <div
                id="site-header-title"
                class="site-header__logo-image"
                v-html="$variable('design.logo_primary')"
              />
            </a>

            <!-- Desktop Navigation Right -->
            <nav
              class="site-header__navigation site-header__navigation--right"
              :aria-label="$string('general.site_header.navigation_right')"
            >
              <ul class="site-header__navigation-list">
                <!-- Search -->
                <li
                  class="
                    site-header__navigation-item
                    site-header__navigation-item--search
                    text-desktop-regular
                  "
                >
                  <button
                    class="
                      site-header__navigation-link
                      site-header__icon--search
                    "
                    type="button"
                    @click="
                      toggleOverlay({
                        component: 'search-drawer',
                        ignoreDismissed: true,
                        namespace: 'searchDrawer',
                      })
                    "
                  >
                    <span
                      class="text-desktop-regular"
                      v-text="$string('general.site_header.search')"
                    />
                  </button>
                </li>

                <!-- Account -->
                <li
                  v-if="displayNavElements.displayAccount"
                  class="
                    site-header__navigation-item
                    site-header__navigation-item--account
                    text-desktop-regular
                  "
                >
                  <a
                    v-if="$variable('customer.loggedIn')"
                    class="site-header__navigation-link"
                    :href="$variable('routes.account.url')"
                    v-text="$string('general.site_header.account')"
                  />

                  <button
                    v-else
                    class="
                      site-header__navigation-link
                      site-header__account-btn
                      text-desktop-regular
                    "
                    type="button"
                    @click="
                      toggleOverlay({
                        component: 'account-forms',
                        ignoreDismissed: true,
                        namespace: 'accountDrawer',
                      })
                    "
                  >
                    <span
                      data-title="$string('general.navigation.account')"
                      v-text="$string('general.navigation.account')"
                    />
                  </button>
                </li>

                <!-- Wishlist -->
                <li
                  v-if="displayNavElements.displayWishlist"
                  class="
                    site-header__navigation-item
                    site-header__navigation-item--wishlist
                    text-desktop-regular
                  "
                >
                  <!--
                    <button
                    class="
                    site-header__navigation-link
                    site-header__account-btn
                    text-desktop-regular
                    "
                    type="button"
                    @click="
                    toggleOverlay({
                    component: 'wishlist-list',
                    ignoreDismissed: true,
                    namespace: 'wishlistDrawer',
                    })
                    "
                    v-html="wishlistString"
                    />
                  -->

                  <button
                    class="site-header__navigation-link
                      site-header__wishlist-btn
                      text-desktop-regular"
                    :class="{
                      [$class('active')]: getActiveOverlay === 'wishlistDrawer',
                      'site-header__menu--active': mobileHeaderActive,
                    }"
                    type="button"
                    :aria-label="$string('general.site_header.wishlist')"
                    @click="
                      toggleOverlay({
                        component: 'wishlist-list',
                        ignoreDismissed: true,
                        namespace: 'wishlistDrawer',
                      })"
                  >
                    <span class="site-header__wishlist-badge__span">

                      <component
                        :is="wishlistIcon"
                      />
                      <component
                        :is="wishlistFilledIcon"
                        class="icon--filled"
                      />
                      <span
                        v-if="getActiveOverlay !== 'wishlistDrawer'"
                        class="site-header__wishlist-badge"
                        aria-hidden="true"
                        v-html="wishlistCount"
                      />
                    </span>
                  </button>
                </li>

                <!-- Bag -->
                <li
                  v-if="displayNavElements.displayBag"
                  class="
                    site-header__navigation-item
                    site-header__navigation-item--bag
                    text-desktop-regular
                  "
                >
                  <button
                    v-if="displayNavElements.displayBag"
                    class="site-header__navigation-link site-header__bag-btn"
                    type="button"
                    :aria-label="$string('general.navigation.bag')"
                    @click="handleCartToggle"
                  >
                    <span class="site-header__cart-badge__span">
                      <icon-bag v-if="!cart.item_count" />
                      <icon-bag-filled v-if="cart.item_count" />

                      <span
                        v-if="cart.item_count"
                        class="site-header__cart-badge"
                        aria-hidden="true"
                        v-text="cart.item_count"
                      />
                    </span>
                  </button>
                </li>
              </ul>
            </nav>

            <!-- Mobile Navigation Right -->
            <nav
              class="
                site-header__navigation-list
                site-header__navigation-list--mobile
              "
              :aria-label="$string('general.site_header.navigation_right')"
            >
              <!-- Wishlist/bookmark icon -->
              <button
                v-if="displayNavElements.displayWishlist"
                class="site-header__menu site-header__icon--wishlist"
                :class="{
                  [$class('active')]: getActiveOverlay === 'wishlistDrawer',
                  'site-header__menu--active': mobileHeaderActive,
                }"
                type="button"
                :aria-label="$string('general.site_header.wishlist')"
                @click="
                  toggleOverlay({
                    component: 'wishlist-list',
                    ignoreDismissed: true,
                    namespace: 'wishlistDrawer',
                  })
                "
              >
                <span class="site-header__wishlist-badge__span">
                  <component
                    :is="wishlistIcon"
                  />
                  <component
                    :is="wishlistFilledIcon"
                    class="icon--filled"
                  />
                  <span
                    v-if="getActiveOverlay !== 'wishlistDrawer'"
                    class="site-header__wishlist-badge"
                    aria-hidden="true"
                    v-html="wishlistCount"
                  />
                </span>
              </button>

              <!-- Bag icon -->
              <button
                v-if="displayNavElements.displayBag"
                class="site-header__menu site-header__icon--bag"
                :class="{
                  [$class('active')]: getActiveOverlay === 'cartDrawer',
                  'site-header__menu--active': mobileHeaderActive,
                }"
                type="button"
                :aria-label="$string('general.site_header.bag')"
                @click="handleCartToggle"
              >
                <icon-close v-if="getActiveOverlay === 'cartDrawer'" />

                <template v-else>
                  <icon-bag />
                  <span
                    v-if="cart.item_count"
                    class="site-header__cart-badge"
                    aria-hidden="true"
                    v-text="cart.item_count"
                  />
                </template>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </header>

    <overlay
      key="menuDrawer"
      block-class="menu-drawer-overlay"
      direction="void"
      namespace="menuDrawer"
      :template="false"
      type="modal"
    >
      <template #body="{ isActive }">
        <menu-drawer
          v-show="getComponentVisible('menu-drawer')"
          v-cloak
          :handle="mobile.handle"
          :is-active="isActive"
          :item-count="mobile.items.length"
          :items="mobile.items"
          :mega-menu-theme="megaMenuTheme"
          :slides="mobileSlides"
        />
      </template>
    </overlay>

    <!-- Search drawer -->
    <overlay
      key="searchDrawer"
      block-class="search-drawer-overlay"
      direction="left"
      namespace="searchDrawer"
      type="drawer"
    >
      <template #body="{ isActive }">
        <search-drawer
          v-cloak
          :is-active="isActive"
          :min-chars="search.minChars"
          :merchant="search.merchant"
          :popular-searches="search.popularSearches"
          :whats-new="search.whatsNew"
          :product-limit="search.productLimit"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Component / Global: Site header
 * -----------------------------------------------------------------------------
 * Site header component, contains menu drawer and meganav.
 * - No style import as it's included in critical stylesheet.
 * @param {Object} desktop - Desktop linklist handle and items.
 * @param {Object} displayNavElements - Shows/hides elements in the nav.
 * @param {Object} headerTheme - Background and content themes for the header.
 * @param {Object} megaMenuTheme - Theme settings for the mega menu.
 * @param {Object} megaNavCta - Mega Nav CTA settings.
 * @param {Object} mobile - Mobile linklist handle and item count.
 * @param {Array} [mobileSlides] - Array of mobile menu slides.
 * @param {Array} [preHeaderBannerContent] - Array of banner slides.
 * @param {Boolean} [preHeaderBannerDismissable] - Sets whether to make banner
 * able to be dismissed.
 * @param {String} preHeaderBannerId - ID for the pre-header banner.
 * @param {Number} [preHeaderBannerInterval] - Pre-header interval in ms.
 * @param {Boolean} [showPreHeaderBanner] - Toggles whether to display the
 * pre-header banner or not.
 * @param {Object} [preHeaderBannerTheme] - Defines background and content for
 * pre-header banner component.
 * @param {Object} [preHeaderTransitionSpeed] - Defines transition speed on pre-
 * header banner in seconds.
 * @param {Array} [slides] - Array of slide objects created from section blocks.
 *
 */
import { isVisible } from '~/helpers/general'

import { nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import MenuDrawer from '~async/menu-drawer/menu-drawer'
import Overlay from '~global/overlay/overlay'
import SearchDrawer from '~async/search-drawer/search-drawer'
import PreHeaderBanner from './pre-header-banner'

import IconBag from '~icons/misc/bag.svg'
import IconBagFilled from '~icons/misc/bag-filled.svg'
import IconBookmarkNav from '~icons/misc/bookmark-nav.svg'
import IconBookmarkNavFilled from '~icons/misc/bookmark-nav-filled.svg'
import IconClose from '~icons/misc/close.svg'
import IconMenu from '~icons/misc/menu.svg'
import IconSearch from '~icons/misc/search.svg'
import IconSearchDesktop from '~icons/misc/search-desktop.svg'

export default {
  name: 'SiteHeader',

  components: {
    Btn,
    IconBag,
    IconBagFilled,
    IconBookmarkNav,
    IconBookmarkNavFilled,
    IconClose,
    IconMenu,
    IconSearch,
    IconSearchDesktop,
    MenuDrawer,
    Overlay,
    PreHeaderBanner,
    SearchDrawer,
  },

  props: {
    desktop: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },

    mediaObjects: {
      type: Object,
      default: () => ({
        handle: 'media-objects',
        items: [],
      }),
    },

    displayNavElements: {
      type: Object,
      default: () => ({
        displayBag: true,
        displayWishlist: true,
        displayAccount: true,
        displaySearch: true,
      }),
    },

    headerTheme: {
      type: Object,
      default: () => ({
        desktop: {
          background: '',
          content: '',
        },
        mobile: {
          background: '',
          content: '',
        },
      }),
    },

    megaMenuTheme: {
      type: Object,
      default: () => ({
        desktop: {
          background: '',
          content: '',
        },
        mobile: {
          background: '',
          content: '',
        },
      }),
    },

    mobile: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },

    mobileSlides: {
      type: Array,
      default: () => [],
    },

    preHeaderBannerContent: {
      type: Array,
      default: () => [],
    },

    preHeaderBannerDismissable: {
      type: Boolean,
      default: false,
    },

    preHeaderBannerId: {
      type: String,
      default: '',
    },

    preHeaderBannerInterval: {
      type: Number,
      default: 3500,
    },

    preHeaderBannerTheme: {
      type: Object,
      default: () => ({
        background: '',
        content: '',
      }),
    },

    preHeaderTransitionSpeed: {
      type: Number,
      default: 1,
    },

    search: {
      type: Object,
      default: () => ({
        apiKey: '',
        filters: '',
        minChars: '',
        popularSearches: '',
        whatsNew: '',
        productLimit: '',
        subdomain: '',
      }),
    },

    showPreHeaderBanner: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      displayMegaMenu: false,
      height: {
        total: 0,
        visible: 0,
        announcement: 0,
      },
      theme: {
        desktop: {
          background: null,
          content: null,
        },
        mobile: {
          background: null,
          content: null,
        },
      },
      menuTheme: false,
      wishlistString: `<!-- include 'wishlist-string' -->`,
      wishlistCount: `<!-- include 'wishlist-count' -->`,
      mediaObject: {
        image: null,
        link: null,
        text: '',
        error: '',
        textColour: '',
        imagePosition: '',
      },
      mediaObjectFallback: null,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      bannerDismissed: (state) => state.header.preHeaderDismissed,
      cart: (state) => state.cart.response,
      headerState: (state) => state.index.scroll.header,
      isMobile: (state) => state.index.screen.mobile,
      loadedStylesheets: (state) => state.index.stylesheets,
      scroll: (state) => state.index.scroll.y,
      breakpoint: (state) => state.index.screen.breakpoint,
    }),

    /**
     * Compute search icon based on overlay state.
     * @returns {String}
     */
    searchIcon() {
      if (this.getActiveOverlay === 'searchDrawer') {
        return 'icon-close'
      }
      return 'icon-search'
    },

    /**
     * Compute wishlist icon based on overlay state.
     * @returns {String}
     */
    wishlistIcon() {
      if (this.getActiveOverlay === 'wishlistDrawer') {
        return 'icon-close'
      }
      return 'icon-bookmark-nav'
    },

    /**
     * Compute wishlist icon based on overlay state.
     * @returns {String}
     */
    wishlistFilledIcon() {
      if (this.getActiveOverlay === 'wishlistDrawer') {
        return 'icon-close'
      }
      return 'icon-bookmark-nav-filled'
    },

    /**
     * Returns the current header theme on desktop.
     * @returns {String}
     */
    headerThemeDesktop() {
      return this.theme.desktop.content
        ? this.theme.desktop.content
        : this.headerTheme.desktop.content
    },

    /**
     * Returns the current header theme on mobile.
     * @returns {String}
     */
    headerThemeMobile() {
      if (this.menuTheme) {
        return this.megaMenuTheme.mobile.content
      }
      return this.theme.mobile.content
        ? this.theme.mobile.content
        : this.headerTheme.mobile.content
    },

    /**
     * Returns whether any of the mobile header overlays are active. of t.
     * @returns {Boolean}
     */
    mobileHeaderActive() {
      const overlays = [
        'menuDrawer',
        'searchDrawer',
        'accountDrawer',
        'wishlistDrawer',
        'cartDrawer',
      ]
      return overlays.includes(this.getActiveOverlay)
    },
  },

  watch: {

    /**
     * Set the global height variables when the breakpoint changes.
     * - needed for ensuring menu drawer is positioned at the correct location.
     */
    breakpoint() {
      this.setHeights()
    },

    /**
     * Set the global height variables when the pre-header banner is dismissed.
     * - needed for ensuring menu drawer is positioned at the correct location.
     */
    bannerDismissed() {
      this.renderBannerStyles()
      setTimeout(() => {
        this.setHeights(true)
      }, this.$timing('normal'))
    },

    /**
     * Watch header state to update height variables.
     * - Wait for site header transition to end before updating height.
     * - Set height immediately if prefers reduced motion.
     */
    headerState() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.setHeights()
        return
      }

      this.$refs.siteHeader.ontransitionstart = (args) => {
        if (!args || args.propertyName !== 'transform') {
          return
        }

        this.setHeights()
      }
    },

    /**
     * Watch total height to update CSS variables.
     */
    'height.total'() {
      const device = this.isMobile ? 'mobile' : 'desktop'

      if (this.headerTheme[device].background === 'transparent') {
        document.documentElement.style.setProperty(
          '--header-static-height',
          '0px',
        )
      } else {
        document.documentElement.style.setProperty(
          '--header-static-height',
          `${this.height.total}px`,
        )
      }

      this.setHeaderHeight(this.height.total)
    },

    /**
     * Watch announcement height to update CSS variable.
     */
    'height.announcement'() {
      document.documentElement.style.setProperty(
        '--announcement-bar-height',
        `${this.height.announcement}px`,
      )
    },

    /**
     * Watch loaded stylesheets to set height.
     */
    loadedStylesheets() {
      if (!this.loadedStylesheets.includes('layout.theme.css')) {
        return
      }

      /**
       * Delay set height to wait for CSS to be parsed.
       */
      setTimeout(() => {
        this.setHeights()
      }, 200)
    },

    /**
     * On scroll we want to check for overlapping sections and update the
     * header theme accordingly.
     * @returns {String}
     */
    scroll: {
      handler() {
        this.updateHeaderTheme()
      },
    },

    /**
     * Watch active overlay to add mobile nav styles to header.
     */
    getActiveOverlay() {
      this.menuTheme = this.getActiveOverlay === 'menuDrawer'

      if (!this.menuTheme) {
        this.updateHeaderTheme()
        return
      }

      /**
       * Set header themes from the overlapped element.
       */
      document.documentElement.style.setProperty(
        '--header-bg-mobile',
        this.megaMenuTheme.mobile.background,
      )
    },
  },

  mounted() {
    nextTick().then(() => {
      this.setHeights(true)
      this.renderBannerStyles()
      this.updateHeaderTheme()
      this.handleMediaBlocks()
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      toggleOverlay: 'overlays/toggle',
      setHeaderHeight: 'header/setHeaderHeight',
      setVisibleHeight: 'header/setHeaderVisibleHeight',
    }),

    revertMediaObject() {
      if (this.mediaObjectFallback) {
        this.updateMediaObject(this.mediaObjectFallback)
      }
    },

    updateMediaObject(data) {
      const linkClass = 'site-header-subnav-media-block__link--white'

      try {
        this.mediaObject.image.src = data.image
        this.mediaObject.link.href = data.url
        this.mediaObject.text.textContent = data.text
        this.mediaObject.image.style.objectPosition = data.imagePosition

        if (data.textColour === 'white') {
          this.mediaObject.link.classList.add(linkClass)
        } else {
          this.mediaObject.link.classList.remove(linkClass)
        }
      } catch (err) {
        this.mediaObject.error = err.message
      }
    },

    handleMediaChange(handle = '') {
      if (typeof handle !== 'string' || handle === '') {
        return
      }

      const mediaObject =
        this.mediaObjects.find(obj => obj.handle === handle) ||
        this.mediaObjectFallback

      if (mediaObject) {
        this.updateMediaObject(mediaObject)
      }
    },

    handleMediaBlocks() {
      const primaryMenu = this.$refs.mediaBlock?.find(item => item.dataset.mediaBlockHandle === 'new-1')

      if (primaryMenu) {
        this.mediaObject.image = primaryMenu.querySelector('[data-block-image]')
        this.mediaObject.link = primaryMenu.querySelector('[data-block-link]')
        this.mediaObject.text = primaryMenu.querySelector('[data-block-link-text]')
        this.captureMediaBlockFallback()
      }
    },

    captureMediaBlockFallback() {
      this.mediaObjectFallback = {
        image: this.mediaObject.image.src,
        url: this.mediaObject.link.href,
        text: this.mediaObject.text.innerText,
        textColour: 'black',
        imagePosition: 'top',
      }

      if (this.mediaObject.link.classList.contains('site-header-subnav-media-block__link--white')) {
        this.mediaObjectFallback.textColour = 'white'
      }
    },

    isSubtitle(tertiary) {
      return tertiary.title_raw.includes('--subtitle')
    },

    isFooter(tertiary) {
      return tertiary.title_raw.includes('--margin')
    },

    isPrivateSale(tertiary) {
      return this.privateSale(tertiary.title_raw)
    },

    isNew(tertiary) {
      return tertiary.title_raw.includes('--new')
    },

    isPublicLink(tertiary) {
      return !tertiary.title_raw.includes('--private')
    },

    privateSale(title) {
      if (!title) {
        return false
      }

      if (title.includes('--private')) {

        if (typeof title === 'undefined') {
          return false
        }

        if (this.$variable('customer.loggedIn') === true) {
          const customerTier = this.$variable('loyalty.tier')
          if (title.includes(customerTier)) {
            return true
          }
        }
      }
      return false
    },

    handleGtm(event) {
      const dataLayer = window.dataLayer || []

      const target = event.currentTarget

      const navLevel = target.dataset.gtmNavLevel
      const navItem = target.closest('[data-gtm-nav-item]')
      const navCategory = navItem?.querySelector('[data-gtm-nav-level="primary"]')?.innerText

      const navSubBlock = target.closest('[data-gtm-subnav-block]')
      const navSubCategory = navLevel === 'primary' ? 'n/a' : navSubBlock?.querySelector('[data-gtm-subnav-block-header]')?.innerText

      dataLayer.push({
        event: 'navigation_click_desktop',
        navigationClickDesktop: {
          level: navLevel,
          category: navCategory,
          sub_category: navSubCategory,
          click_url: target.href,
          click_text: target.innerText,
        },
      })
    },

    /**
     * Applies styles to header to accommodate collapse of pre-header banner in
     * applicable scenarios.
     */
    renderBannerStyles() {
      if (!this.showPreHeaderBanner) {
        return
      }

      if (!this.stickyHeader) {
        return
      }

      const headerSection = document.querySelector(
        '#shopify-section-site-header',
      )
      const headerElement = this.$refs.siteHeader
      const preHeader = document.querySelector('.pre-header-banner')
      if (!headerSection || !headerElement || !preHeader) {
        return
      }

      if (this.showPreHeaderBanner && !this.bannerDismissed) {
        const offset = preHeader?.offsetHeight

        /**
         * Set height for the pre-header banner to css variable.
         */
        document.documentElement.style.setProperty(
          '--pre-header-offset',
          `${offset}px`,
        )

        headerSection.classList.add('site-header-section--sticky')
        return
      }

      /**
       * Set height for the pre-header banner to css variable.
       */
      document.documentElement.style.setProperty('--pre-header-offset', '0')
      headerSection.classList.remove('site-header-section--sticky')
    },

    /**
     * Handles the updating of the header theme.
     */
    updateHeaderTheme() {
      const headerElement = document.querySelector('.site-header')

      if (!headerElement) {
        return
      }

      if (!isVisible(headerElement)) {
        return
      }

      const header = headerElement.getBoundingClientRect()
      const sections = [...document.querySelectorAll('[js-header-scroll-activated="true"]')]
      const overlapping = sections.find((sectionData) => {
        const section = sectionData.closest('section')
        if (this.checkElementsOverlap(header, section)) {
          return section
        }
        return false
      })

      const {
        mobileBackground,
        desktopBackground,
        mobileContent,
        desktopContent,
        transparentHeader,
      } = overlapping?.dataset || {
        mobileBackground: null,
        desktopBackground: null,
        mobileContent: null,
        desktopContent: null,
        transparentHeader: null,
      }

      if (transparentHeader === 'true') {
        headerElement.style.setProperty('--header-bg-mobile', 'transparent')
        headerElement.style.setProperty('--header-bg-desktop', 'transparent')
      } else {
        headerElement.style.setProperty('--header-bg-mobile', mobileBackground)
        headerElement.style.setProperty('--header-bg-desktop', desktopBackground)
      }

      headerElement.style.setProperty(
        '--header-color-mobile',
        mobileContent === 'dark' ? 'var(--color-toteme-white)' : 'var(--color-text-primary)',
      )

      headerElement.style.setProperty(
        '--header-color-desktop',
        desktopContent === 'dark' ? 'var(--color-toteme-white)' : 'var(--color-text-primary)',
      )

      this.theme.mobile.content = mobileContent
      this.theme.desktop.content = desktopContent

    },

    /**
     * Checks if the supplied elements are overlapping vertically.
     * @param {Object} domRect1 - Element one bounding rect.
     * @param {Object} domRect2 - HTMLElement to compare.
     * @returns {Boolean}
     */
    checkElementsOverlap(domRect1, element2) {
      const domRect2 = element2.getBoundingClientRect()
      return (
        domRect1.bottom > domRect2.top && domRect1.bottom < domRect2.bottom
      )
    },

    /**
     * Set heights of fixed header.
     * - Height.total is used for body padding.
     * - Height.visible is used for overlays etc.
     * - Remove setHeights if header is not fixed.
     * @param {Boolean} mounted - If height is being set on mounted.
     */
    setHeights(mounted) {
      if (mounted) {
        this.height.total = this.$refs.siteHeader?.offsetHeight
      }

      if (this.showPreHeaderBanner && this.bannerDismissed === this.preHeaderBannerId) {
        this.height.announcement = 0
      }

      /**
       * Visible height is element height combined with translateY value.
       */
      if (this.$refs.siteHeader.classList.contains('is-collapsed')) {
        this.height.visible = 0
      } else {
        this.height.visible = this.$refs.siteHeader?.offsetHeight
      }
    },

    /**
     * Set heights of announcement bar..
     * @param {Number} value - Announcement bar height.
     */
    updateAnnouncementHeight(value) {
      this.height.announcement = value
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      if (
        this.$variable('page.type') === 'cart' ||
        !this.getComponentExists('cart-drawer')
      ) {
        return
      }

      event.preventDefault()

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })
    },
  },
}
</script>
